// src/pages/AdminEmpresaClientesList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  TextField,
  Chip,
  InputAdornment,
  Tooltip,
  TableContainer,
  Avatar,
  Divider,
  alpha,
  useTheme,
  Snackbar,
  Alert,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import EventRepeatRoundedIcon from "@mui/icons-material/EventRepeatRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import FilterAltOffRoundedIcon from "@mui/icons-material/FilterAltOffRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";

import {
  collection,
  query,
  where,
  onSnapshot,
  deleteDoc,
  doc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../contexts/firebase/config";

function parseDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;

  const d = new Date(value);
  return isNaN(d) ? null : d;
}

function fmtDate(value) {
  const d = parseDate(value);
  return d ? d.toLocaleDateString("pt-PT") : "—";
}

function diasAte(value) {
  const d = parseDate(value);
  if (!d) return null;

  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  const alvo = new Date(d);
  alvo.setHours(0, 0, 0, 0);

  return Math.ceil((alvo - hoje) / (1000 * 60 * 60 * 24));
}

function normalizeText(value) {
  return (value || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

function estadoColor(estado) {
  const st = normalizeText(estado);

  if (st.includes("renov")) return "warning";
  if (st.includes("novo")) return "info";
  if (st.includes("ativo")) return "success";
  if (st.includes("expir")) return "error";

  return "default";
}

function csvValue(value) {
  const safe = value === undefined || value === null ? "" : String(value);
  return `"${safe.replace(/"/g, '""')}"`;
}

function limparNomeFicheiro(value) {
  return (value || "empresa")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9-_]/g, "-")
    .replace(/-+/g, "-")
    .toLowerCase();
}

export default function AdminEmpresaClientesList() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { empresaId } = useParams();

  const colors = {
    accent: theme.palette.primary.main,
    surface: isDark ? "rgba(15,23,42,0.78)" : "rgba(255,255,255,0.78)",
    card: isDark ? "rgba(15,23,42,0.84)" : "rgba(255,255,255,0.84)",
    cardStrong: isDark ? "rgba(15,23,42,0.96)" : "rgba(255,255,255,0.96)",
    border: isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)",
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    hero: isDark ? "rgba(2,6,23,0.96)" : "rgba(15,23,42,0.96)",
  };

  const [filtroStatus, setFiltroStatus] = useState("todos");
  const [filtroAno, setFiltroAno] = useState("todos");
  const [filtroPlano, setFiltroPlano] = useState("todos");
  const [filtroRenovacao, setFiltroRenovacao] = useState("todos");
  const [pesquisa, setPesquisa] = useState("");

  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [dataRenovacao, setDataRenovacao] = useState("");

  const [snack, setSnack] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  useEffect(() => {
    setLoading(true);

    const conditions = [where("empresaId", "==", empresaId)];

    if (filtroStatus === "renovacao") {
      conditions.push(where("estado", "==", "Renovação"));
    } else if (filtroStatus === "novos") {
      conditions.push(where("estado", "==", "Novo"));
    }

    if (filtroAno !== "todos") {
      const year = parseInt(filtroAno, 10);
      const start = Timestamp.fromDate(new Date(`${year}-01-01T00:00:00`));
      const end = Timestamp.fromDate(new Date(`${year}-12-31T23:59:59`));

      conditions.push(where("dataRegisto", ">=", start));
      conditions.push(where("dataRegisto", "<=", end));
    }

    if (["Base", "Flex", "PRO"].includes(filtroPlano)) {
      conditions.push(where("plano", "==", filtroPlano));
    }

    const q = query(collection(db, "clientes"), ...conditions);

    const unsub = onSnapshot(
      q,
      (snap) => {
        setClientes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("Erro ao carregar clientes:", err);
        setLoading(false);
      }
    );

    return unsub;
  }, [empresaId, filtroStatus, filtroAno, filtroPlano]);

  const clientesFiltrados = useMemo(() => {
    const termo = normalizeText(pesquisa);

    let lista = [...clientes];

    if (termo) {
      lista = lista.filter((c) => {
        return (
          normalizeText(c.nome).includes(termo) ||
          normalizeText(c.nif).includes(termo) ||
          normalizeText(c.email).includes(termo) ||
          normalizeText(c.plano).includes(termo) ||
          normalizeText(c.estado).includes(termo) ||
          normalizeText(c.contacto).includes(termo)
        );
      });
    }

    if (filtroRenovacao === "15") {
      lista = lista.filter((c) => {
        const dias = diasAte(c.dataFim);
        return dias !== null && dias >= 0 && dias <= 15;
      });
    }

    if (filtroRenovacao === "30") {
      lista = lista.filter((c) => {
        const dias = diasAte(c.dataFim);
        return dias !== null && dias >= 0 && dias <= 30;
      });
    }

    if (filtroRenovacao === "expirados") {
      lista = lista.filter((c) => {
        const dias = diasAte(c.dataFim);
        return dias !== null && dias < 0;
      });
    }

    lista.sort((a, b) => {
      const diasA = diasAte(a.dataFim);
      const diasB = diasAte(b.dataFim);

      if (diasA !== null && diasB !== null) return diasA - diasB;
      if (diasA !== null) return -1;
      if (diasB !== null) return 1;

      return (a.nome || "").localeCompare(b.nome || "");
    });

    return lista;
  }, [clientes, pesquisa, filtroRenovacao]);

  async function handleDelete(id) {
    if (!window.confirm("Tem certeza que deseja apagar este cliente?")) return;
    await deleteDoc(doc(db, "clientes", id));
  }

  function openModal(cliente) {
    setSelected(cliente);
    setDataRenovacao("");
    setOpen(true);
  }

  async function handleConfirmRenovacao() {
    if (!dataRenovacao || !selected) return;

    const ref = doc(db, "clientes", selected.id);

    await updateDoc(ref, {
      estado: "Renovação",
      dataFim: Timestamp.fromDate(new Date(dataRenovacao)),
      aviso15Enviado: false,
      aviso30Enviado: false,
    });

    setOpen(false);
  }

    function calcularProximaRenovacao(cliente) {
    return fmtDate(cliente.dataFim);
  }

  function limparFiltros() {
    setFiltroStatus("todos");
    setFiltroAno("todos");
    setFiltroPlano("todos");
    setFiltroRenovacao("todos");
    setPesquisa("");
  }

  const totalOriginal = clientes.length;
  const totalFiltrado = clientesFiltrados.length;

  const porEstado = useMemo(() => {
    const acc = {};

    clientesFiltrados.forEach((c) => {
      const k = (c.estado || "—").toString();
      acc[k] = (acc[k] || 0) + 1;
    });

    return acc;
  }, [clientesFiltrados]);

  const metricas = useMemo(() => {
    const novos = clientes.filter((c) => normalizeText(c.estado) === "novo").length;

    const renovacoes = clientes.filter((c) =>
      normalizeText(c.estado).includes("renov")
    ).length;

    const proximos15 = clientes.filter((c) => {
      const dias = diasAte(c.dataFim);
      return dias !== null && dias >= 0 && dias <= 15;
    }).length;

    const proximos30 = clientes.filter((c) => {
      const dias = diasAte(c.dataFim);
      return dias !== null && dias >= 0 && dias <= 30;
    }).length;

    const expirados = clientes.filter((c) => {
      const dias = diasAte(c.dataFim);
      return dias !== null && dias < 0;
    }).length;

    return {
      novos,
      renovacoes,
      proximos15,
      proximos30,
      expirados,
    };
  }, [clientes]);

  const anosDisponiveis = useMemo(() => {
    const anoAtual = new Date().getFullYear();
    const anos = new Set([anoAtual, anoAtual - 1, anoAtual - 2]);

    clientes.forEach((c) => {
      const d = parseDate(c.dataRegisto);
      if (d) anos.add(d.getFullYear());
    });

    return Array.from(anos).sort((a, b) => b - a);
  }, [clientes]);

  const selectSx = {
    borderRadius: "18px",
    bgcolor: colors.cardStrong,
    "& fieldset": { borderColor: colors.border },
    "&:hover fieldset": { borderColor: alpha(colors.accent, 0.38) },
    "&.Mui-focused fieldset": { borderColor: colors.accent, borderWidth: 1 },
  };

  const MetricCard = ({ label, value, icon, tone = "primary" }) => {
    const toneColor =
      tone === "success"
        ? theme.palette.success.main
        : tone === "warning"
        ? theme.palette.warning.main
        : tone === "error"
        ? theme.palette.error.main
        : colors.accent;

    return (
      <Paper
        elevation={0}
        sx={{
          px: 1.6,
          py: 1.25,
          minWidth: { xs: "100%", sm: 230 },
          borderRadius: "26px",
          bgcolor: alpha(theme.palette.background.paper, isDark ? 0.14 : 0.74),
          border: "1px solid rgba(255,255,255,0.18)",
        }}
      >
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Avatar
            sx={{
              width: 38,
              height: 38,
              borderRadius: "14px",
              bgcolor: alpha(toneColor, 0.18),
              color: toneColor,
            }}
          >
            {icon}
          </Avatar>

          <Box>
            <Typography
              sx={{
                fontSize: 12,
                color: "rgba(255,255,255,0.66)",
                fontWeight: 800,
              }}
            >
              {label}
            </Typography>

            <Typography sx={{ fontSize: "1.2rem", fontWeight: 950, color: "#ffffff" }}>
              {loading ? "…" : value}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    );
  };


  function exportarCSV() {
    if (clientesFiltrados.length === 0) {
      setSnack({
        open: true,
        severity: "warning",
        message: "Não existem clientes para exportar com os filtros atuais.",
      });
      return;
    }

    try {
      const headers = [
        "Nome",
        "NIF",
        "Email",
        "Contacto",
        "Plano",
        "Estado",
        "Data Registo",
        "Data Fim",
        "Dias ate fim",
        "Proxima Renovacao",
      ];

      const rows = clientesFiltrados.map((c) => {
        const dias = diasAte(c.dataFim);

        return [
          c.nome || "",
          c.nif || "",
          c.email || "",
          c.contacto || "",
          c.plano || "",
          c.estado || "",
          fmtDate(c.dataRegisto),
          fmtDate(c.dataFim),
          dias === null ? "" : dias,
          calcularProximaRenovacao(c),
        ];
      });

      const csvContent = [
        headers.map(csvValue).join(";"),
        ...rows.map((row) => row.map(csvValue).join(";")),
      ].join("\n");

      const blob = new Blob(["\uFEFF" + csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const dataHoje = new Date().toISOString().slice(0, 10);
      const nomeEmpresa = limparNomeFicheiro(empresaId);

      link.href = url;
      link.download = `clientes-${nomeEmpresa}-${dataHoje}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setSnack({
        open: true,
        severity: "success",
        message: "CSV exportado com sucesso.",
      });
    } catch (error) {
      console.error("Erro ao exportar CSV:", error);
      setSnack({
        open: true,
        severity: "error",
        message: "Erro ao exportar CSV.",
      });
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        px: { xs: 1.25, sm: 2.5, md: 3.5 },
        py: { xs: 2, md: 3.5 },
        background: isDark
          ? "linear-gradient(135deg, #020617 0%, #0f172a 52%, #111827 100%)"
          : "linear-gradient(135deg, #f8fafc 0%, #eef2ff 48%, #f8fafc 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: isDark
            ? `radial-gradient(900px 460px at 10% 5%, ${alpha(
                colors.accent,
                0.18
              )}, transparent 62%), radial-gradient(760px 440px at 92% 12%, ${alpha(
                colors.accent,
                0.12
              )}, transparent 58%)`
            : `radial-gradient(900px 460px at 10% 5%, ${alpha(
                colors.accent,
                0.16
              )}, transparent 62%), radial-gradient(760px 440px at 92% 12%, ${alpha(
                colors.accent,
                0.1
              )}, transparent 58%)`,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: isDark ? 0.16 : 0.3,
          pointerEvents: "none",
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.16) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "linear-gradient(to bottom, black, transparent 76%)",
        }}
      />

      <Box sx={{ position: "relative", zIndex: 1, width: "100%" }}>
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            borderRadius: { xs: "28px", md: "42px" },
            border: `1px solid ${colors.border}`,
            bgcolor: colors.surface,
            backdropFilter: "blur(22px)",
            WebkitBackdropFilter: "blur(22px)",
            boxShadow: isDark
              ? "0 28px 90px rgba(0,0,0,0.36)"
              : "0 24px 80px rgba(15,23,42,0.10)",
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              overflow: "hidden",
              width: "100%",
              mb: 3,
              p: { xs: 2.5, md: 4 },
              borderRadius: "34px",
              bgcolor: colors.hero,
              color: "common.white",
              boxShadow: isDark
                ? "0 24px 70px rgba(0,0,0,0.34)"
                : "0 24px 70px rgba(15,23,42,0.22)",
              "&:before": {
                content: '""',
                position: "absolute",
                width: 560,
                height: 560,
                right: -150,
                top: -220,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${alpha(
                  colors.accent,
                  0.46
                )} 0%, ${alpha(colors.accent, 0.18)} 42%, transparent 70%)`,
              },
            }}
          >
            <Stack
              direction={{ xs: "column", xl: "row" }}
              spacing={3}
              justifyContent="space-between"
              alignItems="stretch"
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "16px",
                      bgcolor: "rgba(255,255,255,0.12)",
                      color: "common.white",
                      border: "1px solid rgba(255,255,255,0.16)",
                    }}
                  >
                    <AdminPanelSettingsRoundedIcon fontSize="small" />
                  </Avatar>

                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: 12,
                      fontWeight: 900,
                      letterSpacing: "0.18em",
                      color: "rgba(255,255,255,0.66)",
                    }}
                  >
                    Empresa · Clientes
                  </Typography>
                </Stack>

                <Typography
                  sx={{
                    fontSize: { xs: "2.15rem", sm: "2.85rem", md: "3.7rem" },
                    fontWeight: 950,
                    lineHeight: 0.98,
                    letterSpacing: "-0.07em",
                    color: "#ffffff",
                  }}
                >
                  Clientes da Empresa
                </Typography>

                <Typography
                  sx={{
                    mt: 1.7,
                    maxWidth: 850,
                    color: "rgba(255,255,255,0.68)",
                    fontSize: { xs: "0.98rem", md: "1.08rem" },
                    lineHeight: 1.75,
                  }}
                >
                  Pesquisa, filtra, renova e gere os clientes associados apenas a esta empresa.
                </Typography>
              </Box>

              <Stack
                direction={{ xs: "row", xl: "column" }}
                spacing={1.25}
                useFlexGap
                flexWrap="wrap"
                sx={{
                  width: { xs: "100%", xl: 300 },
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Button
                  component={RouterLink}
                  to={`/admin/empresa/${empresaId}/info`}
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    height: 42,
                    borderRadius: "999px",
                    px: 2,
                    textTransform: "none",
                    fontWeight: 950,
                    bgcolor: "rgba(255,255,255,0.12)",
                    color: "#ffffff",
                    border: "1px solid rgba(255,255,255,0.18)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.18)" },
                  }}
                >
                  Voltar
                </Button>

                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  component={RouterLink}
                  to={`/admin/empresa/${empresaId}/clientes/new`}
                  sx={{
                    height: 42,
                    borderRadius: "999px",
                    px: 2,
                    fontWeight: 950,
                    textTransform: "none",
                    boxShadow: "0 18px 38px rgba(37,99,235,0.28)",
                  }}
                >
                  Novo Cliente
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<DownloadRoundedIcon />}
                  onClick={exportarCSV}
                  disabled={loading || clientesFiltrados.length === 0}
                  sx={{
                    height: 42,
                    borderRadius: "999px",
                    px: 2,
                    fontWeight: 950,
                    textTransform: "none",
                    bgcolor: "rgba(255,255,255,0.12)",
                    color: "#ffffff",
                    border: "1px solid rgba(255,255,255,0.18)",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.18)",
                      border: "1px solid rgba(255,255,255,0.30)",
                    },
                    "&.Mui-disabled": {
                      color: "rgba(255,255,255,0.42)",
                      borderColor: "rgba(255,255,255,0.12)",
                    },
                  }}
                >
                  Exportar CSV
                </Button>

                <MetricCard
                  label="Resultado atual"
                  value={`${totalFiltrado} de ${totalOriginal}`}
                  icon={<PeopleAltRoundedIcon fontSize="small" />}
                />

                <MetricCard
                  label="Renovações 30 dias"
                  value={metricas.proximos30}
                  icon={<EventAvailableRoundedIcon fontSize="small" />}
                  tone="warning"
                />

                <MetricCard
                  label="Expirados"
                  value={metricas.expirados}
                  icon={<WarningAmberRoundedIcon fontSize="small" />}
                  tone="error"
                />
              </Stack>
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              mb: 3,
              p: { xs: 2.25, md: 2.75 },
              borderRadius: "30px",
              border: `1px solid ${colors.border}`,
              bgcolor: colors.card,
              boxShadow: isDark
                ? "0 24px 64px rgba(0,0,0,0.24)"
                : "0 24px 60px rgba(15,23,42,0.07)",
            }}
          >
            <Stack spacing={2.3}>
              <Stack direction="row" spacing={1.45} alignItems="center">
                <Avatar
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: "17px",
                    bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                    color: colors.accent,
                  }}
                >
                  <FilterAltRoundedIcon />
                </Avatar>

                <Box>
                  <Typography variant="h6" fontWeight={950} sx={{ color: colors.text }}>
                    Filtros e pesquisa
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Refina a listagem por estado, ano, plano, renovação ou texto livre.
                  </Typography>
                </Box>
              </Stack>

              <TextField
                fullWidth
                label="Pesquisar cliente"
                placeholder="Pesquisar por nome, NIF, email, plano, contacto ou estado..."
                value={pesquisa}
                onChange={(e) => setPesquisa(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: pesquisa ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setPesquisa("")}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "20px",
                    bgcolor: colors.cardStrong,
                    "& fieldset": { borderColor: colors.border },
                    "&:hover fieldset": { borderColor: alpha(colors.accent, 0.38) },
                    "&.Mui-focused fieldset": {
                      borderColor: colors.accent,
                      borderWidth: 1,
                    },
                  },
                }}
              />

              <Stack
                direction={{ xs: "column", lg: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", lg: "center" }}
                justifyContent="space-between"
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  useFlexGap
                  flexWrap="wrap"
                >
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>

                    <Select
                      value={filtroStatus}
                      label="Status"
                      onChange={(e) => setFiltroStatus(e.target.value)}
                      sx={selectSx}
                    >
                      <MenuItem value="todos">Todos</MenuItem>
                      <MenuItem value="renovacao">Renovação</MenuItem>
                      <MenuItem value="novos">Novos</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Ano</InputLabel>

                    <Select
                      value={filtroAno}
                      label="Ano"
                      onChange={(e) => setFiltroAno(e.target.value)}
                      sx={selectSx}
                    >
                      <MenuItem value="todos">Todos</MenuItem>
                      {anosDisponiveis.map((y) => (
                        <MenuItem key={y} value={y}>
                          {y}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Plano</InputLabel>

                    <Select
                      value={filtroPlano}
                      label="Plano"
                      onChange={(e) => setFiltroPlano(e.target.value)}
                      sx={selectSx}
                    >
                      <MenuItem value="todos">Todos</MenuItem>
                      <MenuItem value="Base">Base</MenuItem>
                      <MenuItem value="Flex">Flex</MenuItem>
                      <MenuItem value="PRO">PRO</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 210 }}>
                    <InputLabel>Renovação</InputLabel>

                    <Select
                      value={filtroRenovacao}
                      label="Renovação"
                      onChange={(e) => setFiltroRenovacao(e.target.value)}
                      sx={selectSx}
                    >
                      <MenuItem value="todos">Todas</MenuItem>
                      <MenuItem value="15">Próximos 15 dias</MenuItem>
                      <MenuItem value="30">Próximos 30 dias</MenuItem>
                      <MenuItem value="expirados">Expirados</MenuItem>
                    </Select>
                  </FormControl>

                  <Button
                    variant="outlined"
                    startIcon={<FilterAltOffRoundedIcon />}
                    onClick={limparFiltros}
                    sx={{
                      height: 40,
                      borderRadius: "999px",
                      textTransform: "none",
                      fontWeight: 900,
                      bgcolor: colors.cardStrong,
                      borderColor: colors.border,
                    }}
                  >
                    Limpar
                  </Button>
                </Stack>

                {Object.keys(porEstado).length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {Object.entries(porEstado).map(([estado, count]) => (
                      <Chip
                        key={estado}
                        size="small"
                        label={`${estado}: ${count}`}
                        color={estadoColor(estado)}
                        sx={{
                          borderRadius: "999px",
                          fontWeight: 900,
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </Paper>

          {loading ? (
            <Paper
              elevation={0}
              sx={{
                p: 7,
                borderRadius: "30px",
                border: `1px solid ${colors.border}`,
                bgcolor: colors.card,
                textAlign: "center",
              }}
            >
              <CircularProgress />
            </Paper>
          ) : clientesFiltrados.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 7,
                textAlign: "center",
                borderRadius: "30px",
                border: `1px dashed ${colors.border}`,
                bgcolor: colors.card,
              }}
            >
              <Avatar
                sx={{
                  width: 72,
                  height: 72,
                  mx: "auto",
                  mb: 2,
                  borderRadius: "26px",
                  bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                  color: colors.accent,
                }}
              >
                <PeopleAltRoundedIcon />
              </Avatar>

              <Typography variant="h6" sx={{ fontWeight: 950, color: colors.text }}>
                Nenhum cliente encontrado
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                Ajusta os filtros ou altera a pesquisa.
              </Typography>
            </Paper>
          ) : (
            <Paper
              elevation={0}
              sx={{
                borderRadius: "30px",
                border: `1px solid ${colors.border}`,
                overflow: "hidden",
                bgcolor: colors.card,
                boxShadow: isDark
                  ? "0 24px 64px rgba(0,0,0,0.28)"
                  : "0 24px 60px rgba(15,23,42,0.07)",
              }}
            >
              <Box sx={{ px: { xs: 2.25, md: 3 }, py: 2.5 }}>
                <Stack direction="row" spacing={1.45} alignItems="center">
                  <Avatar
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: "17px",
                      bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                      color: colors.accent,
                    }}
                  >
                    <PeopleAltRoundedIcon />
                  </Avatar>

                  <Box>
                    <Typography variant="h6" fontWeight={950} sx={{ color: colors.text }}>
                      Lista de clientes
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                      {totalFiltrado} cliente(s) visível(eis) nesta pesquisa.
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Divider sx={{ borderColor: colors.border }} />

              <TableContainer>
                <Table
                  size="small"
                  sx={{
                    minWidth: 1120,
                    "& thead th": {
                      bgcolor: isDark
                        ? "rgba(2,6,23,0.38)"
                        : "rgba(248,250,252,0.92)",
                      fontWeight: 950,
                      py: 1.7,
                      color: colors.muted,
                      fontSize: 12,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      borderBottom: `1px solid ${colors.border}`,
                    },
                    "& tbody tr": { transition: "0.18s ease" },
                    "& tbody tr:hover": {
                      bgcolor: alpha(colors.accent, isDark ? 0.08 : 0.045),
                    },
                    "& tbody td": {
                      py: 1.55,
                      borderBottom: `1px solid ${colors.border}`,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Cliente</TableCell>
                      <TableCell>NIF</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Plano</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Data Registo</TableCell>
                      <TableCell>Fim/Renovação</TableCell>
                      <TableCell>Próxima Renovação</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {clientesFiltrados.map((c) => {
                      const estado = (c.estado || "—").toString();
                      const plano = (c.plano || "—").toString();
                      const dias = diasAte(c.dataFim);

                      const expirado = dias !== null && dias < 0;
                      const urgente = dias !== null && dias >= 0 && dias <= 15;
                      const proximo = dias !== null && dias > 15 && dias <= 30;

                      return (
                        <TableRow key={c.id} hover>
                          <TableCell>
                            <Stack direction="row" spacing={1.2} alignItems="center">
                              <Avatar
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: "15px",
                                  fontWeight: 950,
                                  bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                                  color: colors.accent,
                                }}
                              >
                                {c.nome?.charAt(0)?.toUpperCase() || "C"}
                              </Avatar>

                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 950, color: colors.text }} noWrap>
                                  {c.nome || "—"}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <BadgeRoundedIcon sx={{ fontSize: 17, color: colors.muted }} />

                              <Typography variant="body2" color="text.secondary">
                                {c.nif || "—"}
                              </Typography>
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={0.75}
                              alignItems="center"
                              sx={{ maxWidth: 260 }}
                            >
                              <EmailRoundedIcon sx={{ fontSize: 17, color: colors.muted }} />

                              <Typography variant="body2" color="text.secondary" noWrap>
                                {c.email || "—"}
                              </Typography>
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              icon={<WorkspacePremiumRoundedIcon />}
                              label={plano}
                              sx={{
                                fontWeight: 900,
                                borderRadius: "999px",
                                bgcolor: alpha(colors.accent, isDark ? 0.14 : 0.07),
                                color: colors.accent,
                                "& .MuiChip-icon": { color: "inherit" },
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={estado}
                              color={estadoColor(estado)}
                              sx={{
                                fontWeight: 900,
                                borderRadius: "999px",
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <CalendarMonthRoundedIcon sx={{ fontSize: 17, color: colors.muted }} />

                              <Typography variant="body2" color="text.secondary">
                                {fmtDate(c.dataRegisto)}
                              </Typography>
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              icon={
                                expirado ? (
                                  <WarningAmberRoundedIcon />
                                ) : (
                                  <EventAvailableRoundedIcon />
                                )
                              }
                              label={
                                dias === null
                                  ? fmtDate(c.dataFim)
                                  : expirado
                                  ? `Expirado há ${Math.abs(dias)} dia(s)`
                                  : `${dias} dia(s)`
                              }
                              color={
                                expirado
                                  ? "error"
                                  : urgente
                                  ? "error"
                                  : proximo
                                  ? "warning"
                                  : "success"
                              }
                              sx={{
                                borderRadius: "999px",
                                fontWeight: 900,
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <EventRepeatRoundedIcon sx={{ fontSize: 17, color: colors.muted }} />

                              <Typography variant="body2" color="text.secondary">
                                {calcularProximaRenovacao(c)}
                              </Typography>
                            </Stack>
                          </TableCell>

                          <TableCell align="right">
                            <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                              <Tooltip title="Ver detalhes">
                                <IconButton
                                  component={RouterLink}
                                  to={`/admin/cliente/${c.id}`}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(colors.accent, isDark ? 0.14 : 0.08),
                                    color: colors.accent,
                                    "&:hover": { bgcolor: colors.accent, color: "#fff" },
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Editar cliente">
                                <IconButton
                                  component={RouterLink}
                                  to={`/admin/cliente/${c.id}/edit`}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(
                                      theme.palette.warning.main,
                                      isDark ? 0.16 : 0.1
                                    ),
                                    color: theme.palette.warning.main,
                                    "&:hover": {
                                      bgcolor: theme.palette.warning.main,
                                      color: "#fff",
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Apagar cliente">
                                <IconButton
                                  onClick={() => handleDelete(c.id)}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(
                                      theme.palette.error.main,
                                      isDark ? 0.16 : 0.1
                                    ),
                                    color: theme.palette.error.main,
                                    "&:hover": {
                                      bgcolor: theme.palette.error.main,
                                      color: "#fff",
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Renovar plano">
                                <IconButton
                                  onClick={() => openModal(c)}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(colors.accent, isDark ? 0.14 : 0.08),
                                    color: colors.accent,
                                    "&:hover": { bgcolor: colors.accent, color: "#fff" },
                                  }}
                                >
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Paper>
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "28px",
            p: 1,
            bgcolor: colors.cardStrong,
            border: `1px solid ${colors.border}`,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: 950, color: colors.text }}>
          Renovar plano de {selected?.nome}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} mt={1}>
            <DialogContentText>
              Escolhe a nova data de fim/renovação para o cliente.
            </DialogContentText>

            <TextField
              label="Nova data de fim"
              type="date"
              fullWidth
              value={dataRenovacao}
              onChange={(e) => setDataRenovacao(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "18px",
                  bgcolor: colors.cardStrong,
                },
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", mb: 1.5 }}>
          <Button
            sx={{ borderRadius: "999px", fontWeight: 900 }}
            onClick={() => setOpen(false)}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            sx={{ borderRadius: "999px", fontWeight: 950, px: 2.5 }}
            onClick={handleConfirmRenovacao}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}